import { useCourseProvider } from "@app/secured/CourseHomePageV2/providers/useCourseProvider";
import {
  ActivityPlayerApi,
  ActivityStatus,
  ActivityType,
  ConfirmDialog,
  isAutoGradedComponentKind,
} from "@cca/components";
import {
  AssetComponentMetadata,
  GradeActivityMetadata,
  ResponseChangeProps,
} from "@cca/components/dist/components/assets/types";
import NotificationModal from "@core/components/NotificationModal";
import { ActivityPlayer } from "@core/componentsV2/ActivityPlayer";
import { useDebouncedAssetProgressSaver } from "@core/componentsV2/ActivityPlayer/hooks/useDebouncedAssetProgressSaver";
import { sumAssetPoints } from "@core/componentsV2/PlayerV2/utils";
import { Feature, RoleType } from "@core/enums";
import { useUserInfo } from "@core/hooks";
import { useConfirmDialog } from "@core/hooksV2";
import { AssetProgressDto, DayOutletContextV2 } from "@core/typesV2";
import { isActivityGradeVisible } from "@core/utilsV2";
import {
  useActivityAssetsQuery,
  useActivityContentQuery,
  useFileProvider,
  useService,
  useSubmitAssetMutation,
} from "@providersV2";
import { useActivityLastVisitedPage } from "@providersV2/useActivityLastVisitedPage";
import { LcmsAssetService } from "@servicesV2";
import { useQueryClient } from "@tanstack/react-query";
import { useFlag } from "@unleash/proxy-client-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { z } from "zod";

import { NotificationState } from "../../CourseDayPage";
import { useActivityAutoStart, useNavigationButton } from "../../hooks";
import { useStartActivity, useSubmitActivity } from "../../providers";

const defaultPage = 1;

const DayOutlet = () => {
  const { mutateAsync: submitAssetAsync } = useSubmitAssetMutation();
  const activityPlayerRef = useRef<ActivityPlayerApi>(null);
  const { initialization: lcmsAssetServiceInit } = useService(LcmsAssetService);

  const { courseId, dayId, activityId } = z
    .object({
      courseId: z.string(),
      dayId: z.string(),
      activityId: z.string(),
    })
    .parse(useParams());

  const userIsLearner = useFlag(Feature.CourseManagementLearn);
  const {
    courseIsGradable,
    activities,
    isActivitiesFetched,
    dayIds,
    dayIndex,
    nextDayLink,
    notificationStates,
    closeNotificationModal,
    openNotificationModal,
  } = useOutletContext<DayOutletContextV2>();

  const currActivityIndex = activities.findIndex(
    (activity) => activity.activityId === activityId,
  );

  const currActivity = activities[currActivityIndex];

  const { progress } = currActivity || {};
  const { status } = progress || {};

  const { mutate: startActivity } = useStartActivity(courseId, dayId);

  const everyPrevActivityIsSubmitted = useMemo(
    () =>
      activities.every(
        (x, index) => index >= currActivityIndex || !!x?.progress?.submittedOn,
      ),
    [activities, currActivityIndex],
  );

  const dialog = useConfirmDialog();

  useActivityAutoStart({
    currActivity,
    isActivitiesFetched,
    userIsLearner,
    previousActivitiesSubmitted: everyPrevActivityIsSubmitted,
    onStart: startActivity,
  });

  const activityTypeValue = useMemo(
    () =>
      currActivity?.type === ActivityType.Lesson ? "lesson" : "assessment",
    [currActivity?.type],
  );

  const noContentMessage = useMemo(
    () => `No content available in this ${activityTypeValue}.`,
    [activityTypeValue],
  );

  const [pageNumber, setPageNumber] = useState(
    userIsLearner ? undefined : defaultPage,
  );

  const activityContentQueryEnabled =
    !!currActivity && (!userIsLearner || status !== ActivityStatus.NotStarted);
  const assetsQueryEnabled = activityContentQueryEnabled;

  const { data: activityContent } = useActivityContentQuery({
    courseId,
    activityId,
    attempt: progress?.attempt,
    enabled: activityContentQueryEnabled,
  });
  const activityRevision = activityContent?.activity;
  const { data: course } = useCourseProvider();
  const activity = activities.find((x) => x.activityId === activityId);

  const fetchContentIntegrationAuth = useCallback(
    async (body: {
      assetId: string;
      programId: number;
      skuId: string;
      redirectUrl: string;
      contentLinksRedirectUrl: string;
    }) => {
      if (!activityRevision) return;

      const { service } = await lcmsAssetServiceInit;
      const templateId =
        course && "templateEvent" in course
          ? course.templateEvent?.correlationId ?? ""
          : "";

      return service.getContentIntegrationAuth(body.programId, {
        assetId: body.assetId,
        skuId: body.skuId,
        redirectUrl: body.redirectUrl,
        contentLinksRedirectUrl: body.contentLinksRedirectUrl,
        templateId,
        templateName: activityRevision.title,
      });
    },
    [lcmsAssetServiceInit, activityRevision, course],
  );

  const lastVisitedPage = useActivityLastVisitedPage({
    courseId,
    activityId,
    userIsLearner,
    activityProgress: activity?.progress,
  });

  const pageCount = activityRevision?.pages?.length;

  useEffect(() => {
    if (
      userIsLearner &&
      lastVisitedPage !== undefined &&
      pageNumber === undefined
    ) {
      setPageNumber(lastVisitedPage);
    }
  }, [userIsLearner, pageNumber, pageCount, lastVisitedPage]);

  const { data: nestedAssetDtos, flatData } = useActivityAssetsQuery({
    courseId,
    activityId,
    attempt: progress?.attempt,
    pageNumber,
    withProgress: userIsLearner,
    totalPages: pageCount,
    enabled: assetsQueryEnabled,
  });

  useEffect(() => {
    if (!nestedAssetDtos) return;

    const hasVisitedAssetsAfterReset = nestedAssetDtos.some((a) => {
      const visitedOn = a.progress?.visitedOn;
      if (!visitedOn || !progress?.resetOn) return false;

      return new Date(visitedOn) > new Date(progress.resetOn);
    });

    if (
      progress?.isReset === true &&
      activityId &&
      pageNumber === 1 &&
      !hasVisitedAssetsAfterReset &&
      notificationStates[activityId] !== NotificationState.Open &&
      notificationStates[activityId] !== NotificationState.Acknowledged
    ) {
      openNotificationModal(activityId);
    }
  }, [
    activityId,
    progress?.isReset,
    progress?.resetOn,
    notificationStates,
    openNotificationModal,
    nestedAssetDtos,
    pageNumber,
  ]);

  const userInfo = useUserInfo();

  // Activity-wide (not page-scoped) asset list, used only to compute the points
  // denominator shown in the header.
  const { data: activityAssetDtos } = useActivityAssetsQuery({
    courseId,
    activityId,
    learnerId: userInfo?.externalId,
    attempt: progress?.attempt,
    withProgress: true,
    totalPages: pageCount,
    enabled: assetsQueryEnabled && !!userInfo?.externalId,
    filterByProgress: true,
  });

  const { debounceSaveResponse, flushChanges } = useDebouncedAssetProgressSaver(
    {
      courseId,
      activityId,
    },
  );

  const submitActivityMutation = useSubmitActivity(courseId, dayId);

  const viewMode = useMemo(() => {
    if (!userInfo) return "preview";

    if (userInfo.currentRole === RoleType.Learner) {
      return status === ActivityStatus.InProgress ? "edit" : "review";
    } else {
      return status === ActivityStatus.PendingGrade ? "review" : "preview";
    }
  }, [userInfo, status]);

  const nextPageButtonProps = useNavigationButton({
    activities,
    currActivityIndex,
    onNextPageButtonClick: () =>
      setPageNumber((prevNumber) => (prevNumber || 0) + 1),
    currPageIsLast: pageCount === 0 || pageNumber === pageCount,
    userIsLearner,
    nextDayLink,
    courseId,
    dayIndex,
    dayIds,
    submitActivityMutation,
    openCompleteDialog: dialog.open,
    viewMode,
    activityPlayerRef,
    flushProgress: flushChanges,
  });

  const queryClient = useQueryClient();

  const onLearnerSubmit = async (assetId: string) => {
    await flushChanges();

    const assetKind = flatData?.find((asset) => asset.id === assetId)?.type;

    // Manually-graded kinds have no auto-grader — `/grade` would 400. The answer
    // is already persisted by flushChanges above; grading happens at activity
    // submit.
    if (assetKind && !isAutoGradedComponentKind(assetKind)) {
      return null;
    }

    return submitAssetAsync({ courseId, activityId, assetId });
  };

  const { getFile, saveFileMutation, deleteFileMutation } = useFileProvider();

  const allAssetsViewed = useMemo(
    () =>
      viewMode === "edit"
        ? nestedAssetDtos?.every((x) => !!x.progress?.visitedOn)
        : true,
    [nestedAssetDtos, viewMode],
  );

  if (userInfo == null || activities.length === 0) return null;
  const onLearnerResponseChange = ({
    assetId,
    response,
    visitedOn,
  }: ResponseChangeProps) => {
    queryClient.setQueryData<AssetProgressDto[]>(
      ["assets", "progress", { courseId, activityId, learnerId: null }],
      (old = []) => {
        const currentIdx = old.findIndex((p) => p.assetId === assetId);

        // A missing `response` means "keep what is stored" — the same rule
        // `debounceSaveResponse` follows, so a view-tracker ping that only
        // carries `visitedOn` cannot wipe a cached answer.
        const patch: Partial<Omit<AssetProgressDto, "assetId">> = {
          ...(response !== undefined && { response: { value: response } }),
          ...(visitedOn !== undefined && { visitedOn }),
        };

        let next = old.slice();

        if (currentIdx === -1) {
          next = [...next, { assetId, ...patch }];
        } else {
          next[currentIdx] = { ...next[currentIdx], ...patch };
        }

        if (visitedOn !== undefined) {
          const lastAssetId = nestedAssetDtos?.[nestedAssetDtos.length - 1]?.id;

          if (assetId === lastAssetId) {
            const existingIds = new Set(next.map((item) => item.assetId));

            const newEntries = (nestedAssetDtos ?? [])
              .filter((asset) => !existingIds.has(asset.id))
              .map((asset) => ({ assetId: asset.id, visitedOn }));

            next = [
              ...next.map((item) =>
                item.visitedOn === undefined ? { ...item, visitedOn } : item,
              ),
              ...newEntries,
            ];
          }
        }

        return next;
      },
    );

    debounceSaveResponse(assetId, response, visitedOn);
  };

  const handleBackButton = async () => {
    await flushChanges();
    setPageNumber((p) => p! - 1);
  };

  const metadata: AssetComponentMetadata = {
    role: userInfo.currentRole,
    mode: viewMode,
    courseIsGradable,
    activityName: currActivity.title,
    activityType: currActivity.type,
    activityStatus: status,
  };

  const gradeMetadata: GradeActivityMetadata = {
    courseIsCompleted: undefined,
    pointsEarned: progress?.pointsEarned,
    pointsCalculated: progress?.pointsCalculated ?? 0,
    pointsModifier: progress?.pointsModifier,
    // Same activity-wide sum the gradebook uses, so the learner and the grader
    // read the same denominator regardless of which page is open.
    pointsAvailable: activityAssetDtos?.length
      ? sumAssetPoints(activityAssetDtos)
      : progress?.pointsAvailable,
    teacherFeedback: progress?.feedback,
    gradePercentage: progress?.currentGradeInPercentage ?? undefined,
  };

  return (
    <>
      <ActivityPlayer key={pageNumber} ref={activityPlayerRef}>
        <ActivityPlayer.Header
          gradeMetadata={gradeMetadata}
          gradeVisible={isActivityGradeVisible(progress)}
          metadata={metadata}
        />
        <ActivityPlayer.Content
          fetchContentIntegrationAuth={fetchContentIntegrationAuth}
          fileHandlers={{
            getFile,
            saveFile: saveFileMutation.mutateAsync,
            deleteFile: deleteFileMutation.mutateAsync,
          }}
          metadata={metadata}
          nestedAssetDtos={assetsQueryEnabled ? nestedAssetDtos : undefined}
          noContentMessage={noContentMessage}
          onLearnerResponseChange={onLearnerResponseChange}
          onLearnerSubmit={onLearnerSubmit}
        />
        <ActivityPlayer.Footer
          currentPage={pageNumber}
          onPageChange={setPageNumber}
          pageLimit={(lastVisitedPage ?? 0) + (allAssetsViewed ? 1 : 0)}
          totalPages={pageCount}
          viewMode={viewMode}
        >
          <>
            <ActivityPlayer.PreviousButton
              disabled={pageNumber === 1}
              onClick={handleBackButton}
            />

            <ActivityPlayer.NextButton
              loading={submitActivityMutation.isPending}
              {...nextPageButtonProps}
              disabled={allAssetsViewed == false}
            />
          </>
        </ActivityPlayer.Footer>
      </ActivityPlayer>

      <ConfirmDialog
        confirmBtnText="Submit"
        hasCloseButton={false}
        onCancel={dialog.close}
        onConfirm={dialog.confirm}
        show={dialog.isOpen}
        size="xs"
        title="Are you sure?"
      >
        After submitting you will not be able to change your responses. Are you
        sure you still want to submit this
        {` ${activityTypeValue} `}
        for review?
      </ConfirmDialog>

      {activity &&
        notificationStates &&
        notificationStates[activity.activityId] === "open" && (
          <NotificationModal
            iconType="circle-info"
            onClose={() => closeNotificationModal(activity.activityId)}
          >
            This {activity.type?.toLowerCase()} has been reset by your grader.
            Please resubmit the {activity.type?.toLowerCase()}.
          </NotificationModal>
        )}
    </>
  );
};

const DayOutletWrapper = () => {
  const { activityId } = z
    .object({
      activityId: z.string(),
    })
    .parse(useParams());

  return <DayOutlet key={activityId} />;
};

export default DayOutletWrapper;
